/**
 * Thin adapter over @mp/audio (procedural WebAudio). The module is resolved lazily through
 * an injected loader, so a missing or partial audio package never breaks boot or the build.
 *
 * City themes (launch plan Phase 1 step 6): a city pack's music file (`music/theme.json`) is that
 * city's own map and play loops. The pack manager hands it over (`setCityTheme`) as it registers
 * the pack, and the map and play scenes name the city with each track (`music('map', city)`): in
 * a city with a theme the engine plays its loops, rush and shop stay global, and every other city
 * plays the defaults. Themes handed over before the engine loads (the first tap) wait here.
 */
export interface AudioEngine {
  unlock(): void | Promise<void>;
  play(name: string, opts?: { step?: number }): void;
  /** `city`: the city the player is in (its theme, when it has one, replaces map and play). */
  music(track: string | null, city?: string | null): void;
  /**
   * A city's own map and play loops (its pack's music file as parsed JSON; null drops them).
   * Returns the file's problems: one that does not pass plays the defaults. An engine without
   * themes (an older or partial audio package) leaves it out and plays the defaults everywhere.
   */
  setCityTheme?(city: string, theme: unknown): string[];
  setVolumes(v: { sfx?: number; music?: number; master?: number }): void;
  setMuted(muted: boolean): void;
  /** Fade out, stop the music timer and suspend the context. */
  suspend(): void;
  /** Undo suspend(): the context runs again and the music carries on along its beat grid. */
  resume(): void | Promise<void>;
}

type AudioModule = { createAudio?: () => AudioEngine };

/** Game event names → @mp/audio voice names ('' = silent on purpose). */
const ALIASES: Record<string, string> = {
  collect: 'tap',
  check: 'serve',
  upgrade: 'build',
  undo: 'booster',
  shuffle: 'booster',
  magnet: 'booster',
  ice: 'tap',
  crate: 'land',
  whoosh: 'button',
  levelup: 'unlock',
  combo: '',
};

export class AudioAdapter {
  private engine: AudioEngine | null = null;
  private loading: Promise<void> | null = null;
  private sound = true;
  private musicOn = true;
  private track: string | null = null;
  /** the city the track plays in (map and play take its theme, when it has one) */
  private city: string | null = null;
  /** every city theme handed over this session, for an engine that loads later */
  private themes = new Map<string, unknown>();
  /** ads on screen: the AdsService wrap and the H5 SDK's beforeAd/afterAd overlap, so they are counted */
  private ads = 0;
  private hidden = false;

  constructor(private loader: (() => Promise<AudioModule>) | null) {}

  get ready(): boolean {
    return this.engine !== null;
  }

  /** Game sound is held: an ad is on screen or the page is in the background. */
  get paused(): boolean {
    return this.ads > 0 || this.hidden;
  }

  /** Call from a user gesture (browsers keep AudioContext suspended until then). */
  unlock(): void {
    if (this.engine) {
      void this.engine.unlock();
      return;
    }
    if (!this.loader || this.loading) return;
    this.loading = this.loader()
      .then((m) => {
        if (typeof m.createAudio !== 'function') return;
        this.engine = m.createAudio();
        for (const [city, theme] of this.themes) this.giveTheme(city, theme);
        // an ad or the background began while the module loaded: the engine comes up held
        if (this.paused) this.engine.suspend();
        void this.engine.unlock();
        if (!this.paused) this.apply();
      })
      .catch((e: unknown) => console.warn('[audio] unavailable:', e));
  }

  play(name: string, step?: number): void {
    const voice = ALIASES[name] ?? name;
    if (this.sound && voice && !this.paused) this.engine?.play(voice, step === undefined ? undefined : { step });
  }

  /**
   * The looping track in `city` (null: no city, the defaults). While paused this only records
   * them; resuming plays them.
   */
  music(track: string | null, city: string | null = null): void {
    this.track = track;
    this.city = city;
    if (!this.paused) this.engine?.music(this.musicOn ? track : null, city);
  }

  /** The track and city asked for last, and whether a theme was handed over for that city (test hooks). */
  get tune(): { track: string | null; city: string | null; themed: boolean } {
    return { track: this.track, city: this.city, themed: this.city !== null && this.themes.has(this.city) };
  }

  /**
   * A city pack's music file (parsed JSON) as that city's map and play loops; null drops them.
   * Kept for the session, so an engine that loads later gets it too. When the player is in that
   * city right now, the engine fades over to it.
   */
  setCityTheme(city: string, theme: unknown): void {
    if (theme === null) this.themes.delete(city);
    else this.themes.set(city, theme);
    this.giveTheme(city, theme);
  }

  private giveTheme(city: string, theme: unknown): void {
    const problems = this.engine?.setCityTheme?.(city, theme) ?? [];
    if (problems.length) console.warn(`[audio] ${city}'s music file does not pass (the default tracks play there):`, problems.slice(0, 5));
  }

  /** The player's own settings; while paused they are recorded and applied on resume. */
  configure(sound: boolean, music: boolean): void {
    this.sound = sound;
    this.musicOn = music;
    if (!this.paused) this.apply();
  }

  /** Before an ad shows (reference counted: pair each call with resumeAfterAd). */
  pauseForAd(): void {
    this.hold(this.ads + 1, this.hidden);
  }

  /** After the ad closed, however it ended; the sound comes back when the last hold lets go. */
  resumeAfterAd(): void {
    this.hold(Math.max(0, this.ads - 1), this.hidden);
  }

  /** The page went to the background (visibilitychange) or came back. */
  setHidden(hidden: boolean): void {
    this.hold(this.ads, hidden);
  }

  /** Suspend or resume the engine when the paused state flips. */
  private hold(ads: number, hidden: boolean): void {
    const was = this.paused;
    this.ads = ads;
    this.hidden = hidden;
    if (!this.engine || was === this.paused) return;
    if (this.paused) {
      this.engine.suspend();
      return;
    }
    // the player's settings as they are now (they may have changed meanwhile): never louder than chosen
    this.apply();
    void this.engine.resume();
  }

  private apply(): void {
    if (!this.engine) return;
    this.engine.setVolumes({ sfx: this.sound ? 1 : 0, music: this.musicOn ? 0.6 : 0 });
    this.engine.setMuted(!this.sound && !this.musicOn);
    this.engine.music(this.musicOn ? this.track : null, this.city);
  }
}
